// src/pages/SearchOrder.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import OrderCard from '../components/OrderCard';
import Header from '../components/Header';
import t from '../i18n';

function useIsMobile(breakpoint = 640) {
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < breakpoint);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, [breakpoint]);
    return isMobile;
}

const SearchOrder = () => {
    const { orderNumber }           = useParams();
    const isMobile                  = useIsMobile();
    const navigate                  = useNavigate();
    const [orderResult, setOrderResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [widgetVisible, setWidgetVisible] = useState(false);
    const [hovered, setHovered]     = useState(false);
    const [heroQuery, setHeroQuery] = useState('');

    const fetchOrder = async (number) => {
        try {
            const response = await fetch(`${config.api.host}/api/order/${number}`);
            if (!response.ok) throw new Error(`${response.status}`);
            return await response.json();
        } catch (error) {
            console.error('Ошибка при запросе:', error);
            return null;
        }
    };

    const handleSearch = useCallback(async (number) => {
        setOrderResult(null);
        setErrorMessage('');
        const result = await fetchOrder(number);
        if (!result || Object.keys(result).length === 0) {
            setErrorMessage(t.searchNotFound);
        } else {
            setOrderResult(result);
        }
    }, []);

    useEffect(() => {
        if (orderNumber) handleSearch(orderNumber);
    }, [orderNumber, handleSearch]);

    useEffect(() => {
        const handler = (event) => {
            if (event.data === 'paymentSuccess') {
                setWidgetVisible(false);
                window.location.reload();
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const handleHeroSearch = () => {
        const q = heroQuery.trim();
        if (q) navigate('/' + q);
    };

    const handlePayment = () => {
        localStorage.setItem('order', JSON.stringify(orderResult));
        setWidgetVisible(true);
    };

    // ── ГЛАВНАЯ СТРАНИЦА (/) ──────────────────────────────────────────────────

    if (!orderNumber) {
        return (
            <div style={s.page}>
                <Header />

                <div style={s.hero}>
                    <div style={s.heroInner}>

                        {/* Заголовок */}
                        <h1 style={s.heroTitle}>{t.heroTitle}</h1>
                        <p style={s.heroSubtitle}>{t.heroSubtitle}</p>

                        {/* Большое поле поиска */}
                        <div style={s.heroSearchWrap}>
                            <svg width="20" height="20" fill="none" viewBox="0 0 24 24"
                                 stroke="#6b7280" strokeWidth={2} style={{ flexShrink: 0 }}>
                                <circle cx="11" cy="11" r="8"/>
                                <path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
                            </svg>
                            <input
                                style={s.heroInput}
                                placeholder={t.headerPlaceholder}
                                value={heroQuery}
                                onChange={e => setHeroQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleHeroSearch()}
                                autoComplete="off"
                                spellCheck="false"
                                autoFocus
                            />
                            {heroQuery && (
                                <button onClick={() => setHeroQuery('')} style={s.clearBtn}>
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
                                         stroke="#6b7280" strokeWidth={2}>
                                        <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            )}
                            <button onClick={handleHeroSearch} style={s.heroBtn}>
                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
                                     stroke="white" strokeWidth={2} style={{ flexShrink: 0 }}>
                                    <circle cx="11" cy="11" r="8"/>
                                    <path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
                                </svg>
                                {!isMobile && (
                                    <span style={s.heroBtnText}>{t.searchButton}</span>
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    // ── СТРАНИЦА ОРДЕРА (/:orderNumber) ──────────────────────────────────────

    return (
        <div style={s.page}>
            <Header />

            <div style={s.content}>
                {!orderResult && !errorMessage && (
                    <div style={s.loading}>{t.loading}</div>
                )}
                {errorMessage && (
                    <div style={s.error}>{errorMessage}</div>
                )}
                {orderResult && (
                    <OrderCard order={orderResult} onPay={handlePayment} />
                )}
            </div>

            {/* Виджет оплаты в iframe */}
            {widgetVisible && (
                <div style={s.overlay}>
                    <div style={s.widgetContainer}>
                        <iframe src="/pay" title="Payment Widget" style={s.iframe} />
                        <button
                            onClick={() => setWidgetVisible(false)}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                            style={{ ...s.closeBtn, color: hovered ? '#fff' : '#aaa' }}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Стили ─────────────────────────────────────────────────────────────────────

const s = {
    page: {
        backgroundColor: '#121212',
        color:           '#ffffff',
        minHeight:       '100vh',
        display:         'flex',
        flexDirection:   'column',
    },

    // Hero
    hero: {
        flex:           1,
        display:        'flex',
        alignItems:     'flex-start',
        justifyContent: 'center',
        padding:        '48px 16px 32px',
    },
    heroInner: {
        width:    '100%',
        maxWidth: 640,
        display:  'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    heroTitle: {
        fontSize:     '2.4rem',
        fontWeight:   700,
        color:        '#ffffff',
        marginBottom: 10,
        marginTop:    0,
        letterSpacing: '-0.5px',
        textAlign:    'center',
    },
    heroSubtitle: {
        fontSize:     15,
        color:        '#666',
        marginBottom: 28,
        marginTop:    0,
        textAlign:    'center',
    },
    heroSearchWrap: {
        width:           '100%',
        display:         'flex',
        alignItems:      'center',
        gap:             10,
        backgroundColor: '#1e1e1e',
        border:          '1px solid #2a2a2a',
        borderRadius:    12,
        padding:         '12px 10px 12px 14px',
        boxSizing:       'border-box',
    },
    heroInput: {
        flex:       1,
        background: 'transparent',
        border:     'none',
        outline:    'none',
        color:      '#e0e0e0',
        fontSize:   15,
        minWidth:   0,
    },
    clearBtn: {
        flexShrink:  0,
        background:  'transparent',
        border:      'none',
        cursor:      'pointer',
        padding:     4,
        display:     'flex',
        alignItems:  'center',
    },
    heroBtn: {
        flexShrink:      0,
        backgroundColor: '#6200ea',
        border:          'none',
        borderRadius:    8,
        padding:         '8px 16px',
        cursor:          'pointer',
        display:         'flex',
        alignItems:      'center',
        gap:             6,
    },
    heroBtnText: {
        color:      'white',
        fontSize:   14,
        fontWeight: 600,
    },

    // Order result page
    content: {
        flex:    1,
        padding: '16px 16px 32px',
    },
    loading: {
        textAlign:  'center',
        color:      '#555',
        marginTop:  48,
        fontSize:   14,
    },
    error: {
        textAlign:  'center',
        color:      '#ef5350',
        marginTop:  48,
        fontSize:   14,
    },

    // Payment widget overlay
    overlay: {
        position:        'fixed',
        top:             0,
        left:            0,
        width:           '100%',
        height:          '100%',
        backgroundColor: 'rgba(0,0,0,0.75)',
        display:         'flex',
        justifyContent:  'center',
        alignItems:      'center',
        zIndex:          1000,
    },
    widgetContainer: {
        position:        'relative',
        width:           '100%',
        maxWidth:        320,
        height:          '100%',
        maxHeight:       568,
        backgroundColor: '#fff',
        border:          '1px solid #333',
        borderRadius:    12,
        overflow:        'hidden',
        boxShadow:       '0 8px 32px rgba(0,0,0,0.5)',
    },
    iframe: {
        width:  '100%',
        height: '100%',
        border: 'none',
    },
    closeBtn: {
        position:        'absolute',
        top:             10,
        right:           10,
        backgroundColor: 'transparent',
        border:          'none',
        borderRadius:    '50%',
        width:           28,
        height:          28,
        cursor:          'pointer',
        fontSize:        16,
        display:         'flex',
        justifyContent:  'center',
        alignItems:      'center',
    },
};

export default SearchOrder;
